import { SearchFilterV4 } from "@/types/SearchFilter";
import { formatDateForApi, newDateWithCurrentTimezone } from "@/utils";
import { Button } from "@components/ui/button";
import { Calendar } from "@components/ui/calendar";
import {
  DialogHeader,
  DialogFooter,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@components/ui/dialog";
import { Trans } from "@lingui/react/macro";
import { DialogProps } from "@radix-ui/react-dialog";
import { useMemo, useState } from "react";
import { DateRange } from "react-day-picker";

export default function DialogCustomDates({
  minStartDate,
  maxEndDate,
  onChange,
  startDate,
  endDate,
  ...rest
}: {
  onChange: (value: SearchFilterV4, clear?: boolean) => void;
  startDate: SearchFilterV4["start_date"];
  endDate: SearchFilterV4["end_date"];
  minStartDate?: string;
  maxEndDate?: string;
} & DialogProps) {
  // Local state
  const defaultValue = useMemo(() => {
    const initialStartDate = startDate
      ? newDateWithCurrentTimezone(startDate)
      : undefined;
    const initialEndDate = endDate
      ? newDateWithCurrentTimezone(endDate)
      : undefined;

    return initialStartDate && initialEndDate
      ? { from: initialStartDate, to: initialEndDate }
      : { from: undefined, to: undefined };
  }, [startDate, endDate]);

  const [dateRange, setDateRange] = useState<DateRange | undefined>(
    defaultValue
  );
  const [month, setMonth] = useState<Date | undefined>(defaultValue.from);

  // Side-effects
  const [prevDefaultValue, setPrevDefaultValue] = useState(defaultValue);
  const [prevOpen, setPrevOpen] = useState(rest.open);
  if (defaultValue !== prevDefaultValue || rest.open !== prevOpen) {
    setPrevDefaultValue(defaultValue);
    setPrevOpen(rest.open);
    setDateRange(defaultValue);

    if (defaultValue.from) {
      setMonth(defaultValue.from);
    }
  }

  // Helpers
  const isDirty =
    dateRange?.from &&
    dateRange?.to &&
    (defaultValue?.from !== dateRange.from ||
      defaultValue?.to !== dateRange.to);

  return (
    <Dialog {...rest}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            <Trans>Datumspann</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>Filtrera omnämnanden på publiceringsdatum</Trans>
          </DialogDescription>
        </DialogHeader>

        <Calendar
          mode="range"
          month={month}
          showOutsideDays={false}
          onMonthChange={setMonth}
          classNames={{ root: "w-full" }}
          captionLayout="dropdown"
          selected={dateRange}
          onSelect={setDateRange}
          numberOfMonths={2}
          className="rounded-lg border"
          disabled={[
            ...(minStartDate
              ? [{ before: newDateWithCurrentTimezone(minStartDate) }]
              : []),
            ...(maxEndDate
              ? [{ after: newDateWithCurrentTimezone(maxEndDate) }]
              : []),
          ]}
        />

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">
              <Trans>Avbryt</Trans>
            </Button>
          </DialogClose>
          <DialogClose
            disabled={!isDirty}
            onClick={() => {
              onChange({
                start_date: dateRange?.from
                  ? formatDateForApi(dateRange.from)
                  : undefined,
                end_date: dateRange?.to
                  ? formatDateForApi(dateRange.to)
                  : undefined,
              });
            }}
            asChild
          >
            <Button>
              <Trans>Applicera</Trans>
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
